import React, { useCallback, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
// Relative, not "@/...": no "@" alias on the Jest side — same reasoning as the sibling
// NotificationSettingsCard.
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";

// The SAME rule the server enforces in `tenant_identity.validated_practice_name`: trimmed,
// 2..120, and never empty. Blank is meaningful only at SIGNUP, where it means "derive it
// from my email"; a practitioner on this card is deliberately renaming a practice that
// already exists, and `tenants.name` — the public brand, the signature on every email the
// practice sends, the descriptor its clients read on their card — has no sensible empty
// state. The client half exists to answer instantly, not to be the authority: the 422
// branch below reports the same fact when the server is the one that catches it.
const PRACTICE_NAME_SCHEMA = z.object({
  name: z.string().trim()
    .min(2, "settings.practiceName.tooShort")
    .max(120, "settings.practiceName.tooLong"),
});

/**
 * Lets a practice rename itself.
 *
 * Until this existed, `tenants.name` was written once at signup and by no other
 * practitioner-facing path, so a name typed in the first thirty seconds of buying was
 * permanent. The card owns its own read and write rather than joining SettingsAdmin's
 * form: that form POSTs `/admin/settings`, a tenant-scoped key/value store, while the
 * name lives on the GLOBAL `tenants` document behind `/tenant/practice-name`.
 */
export default function PracticeNameCard() {
  const t = useT("admin");
  const tc = useT("common");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const [name, setName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    // `/saas/my-tenant` and not `/saas/tenant/{slug}`: it is the one endpoint that
    // resolves the practice the AUTHENTICATED practitioner acts for, through the owner
    // binding, rather than whichever slug the page happens to know about.
    const { data } = await api.get("/saas/my-tenant");
    if (!data || typeof data !== "object" || Array.isArray(data)) throw payloadShapeError();
    setName(typeof data.name === "string" ? data.name : "");
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  const save = async () => {
    const trimmed = (name || "").trim();
    if (validate(PRACTICE_NAME_SCHEMA, { name: trimmed })) {
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    setBusy(true);
    setSaved(false);
    try {
      const { data } = await api.put("/tenant/practice-name", { name: trimmed });
      setName(data?.name ?? trimmed);
      setSaved(true);
    } catch (e) {
      toast.error(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="practice-name-card"
      className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-7 space-y-5 max-w-3xl"
    >
      <div>
        <h2 className="font-display text-2xl text-eh-ink">{t("settings.practiceName.title")}</h2>
        <p className="text-eh-muted text-sm mt-1">{t("settings.practiceName.subtitle")}</p>
      </div>

      {loadError ? (
        <LoadFailure
          message={extractApiError(loadError, tc("errors.loadFailed"), tc)}
          onRetry={reload}
          retryLabel={tc("actions.retry")}
          testId="practice-name-load-failure"
          retryTestId="practice-name-retry"
        />
      ) : name === null ? (
        <Loader2 className="animate-spin text-eh-muted" size={18} />
      ) : (
        <>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-eh-muted block mb-1">
              {t("settings.practiceName.label")}
            </label>
            <input
              type="text"
              maxLength={120}
              data-testid="practice-name-input"
              value={name}
              onChange={(e) => { setName(e.target.value); clearFieldError("name"); setSaved(false); }}
              className={`w-full bg-white border rounded-xl px-3 py-2 text-sm text-eh-ink outline-none focus:border-eh-sage-deep ${fieldErrors.name ? invalidInputClass : "border-eh-border-soft"}`}
            />
            <FieldError
              message={fieldErrors.name ? t(fieldErrors.name) : ""}
              testId="practice-name-input-error"
            />
            {/* Says what a rename does NOT reach, because both are surprising: an issued
                receipt records who issued it on that date and must not change, and a
                practice that has filed a tax profile takes its receipt name from there. */}
            <p className="text-xs text-eh-muted mt-2" data-testid="practice-name-hint">
              {t("settings.practiceName.hint")}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="practice-name-save"
              onClick={save}
              disabled={busy}
              className="btn-primary text-sm"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}{" "}
              {t("settings.practiceName.save")}
            </button>
            {saved && (
              <span className="text-sm text-eh-sage-deep" data-testid="practice-name-saved">
                ✓ {t("settings.practiceName.saved")}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
